import React, { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { IconType } from 'react-icons';
import {
  FaArrowUp,
  FaArrowDown,
  FaClock,
  FaExclamationCircle,
} from 'react-icons/fa';

// Stat Card Component
interface StatCardProps {
  title: string;
  value: string | number;
  subtitle?: string;
  icon?: IconType;
  iconBgColor?: string;
  iconColor?: string;
  trend?: {
    value: number;
    isPositive: boolean;
    label?: string;
  };
  onClick?: () => void;
  /** Route (with query string) to the list page this number came from. */
  to?: string;
}

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  subtitle,
  icon: Icon,
  iconBgColor = 'bg-blue-100',
  iconColor = 'text-blue-600',
  trend,
  onClick,
  to,
}) => {
  // A `to` renders the whole card as a link so the number and its list page
  // stay one target; `onClick` still works for cards that act in place.
  const Wrapper: any = to ? Link : 'div';
  const wrapperProps = to ? { to } : {};
  return (
    <Wrapper
      {...wrapperProps}
      onClick={onClick}
      className={`block bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow ${
        onClick || to ? 'cursor-pointer' : ''
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide truncate">
            {title}
          </p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{value}</p>
          {subtitle && (
            <p className="mt-1 text-xs text-gray-500 truncate">{subtitle}</p>
          )}
          {trend && (
            <div className="mt-2 flex items-center gap-1">
              {trend.isPositive ? (
                <FaArrowUp className="w-3 h-3 text-green-500" />
              ) : (
                <FaArrowDown className="w-3 h-3 text-red-500" />
              )}
              <span
                className={`text-xs font-medium ${
                  trend.isPositive ? 'text-green-600' : 'text-red-600'
                }`}
              >
                {trend.value}%
              </span>
              {trend.label && (
                <span className="text-xs text-gray-500">{trend.label}</span>
              )}
            </div>
          )}
        </div>
        {Icon && (
          <div className={`p-3 rounded-lg ${iconBgColor}`}>
            <Icon className={`w-5 h-5 ${iconColor}`} />
          </div>
        )}
      </div>
    </Wrapper>
  );
};

// Mini Stat Card for compact displays
interface MiniStatProps {
  label: string;
  value: string | number;
  color?: 'blue' | 'green' | 'yellow' | 'red' | 'gray';
  /** Route (with query string) to the list page this number came from. */
  to?: string;
  /** Open `to` in a new browser tab. */
  newTab?: boolean;
}

export const MiniStat: React.FC<MiniStatProps> = ({
  label,
  value,
  color = 'blue',
  to,
  newTab,
}) => {
  const colors = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
  };

  const body = (
    <>
      <p className="text-xs font-medium opacity-80">{label}</p>
      <p className="text-lg font-bold">{value}</p>
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        {...(newTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        className={`block px-3 py-2 rounded-lg border transition-shadow hover:shadow-md ${colors[color]}`}
      >
        {body}
      </Link>
    );
  }

  return (
    <div className={`px-3 py-2 rounded-lg border ${colors[color]}`}>{body}</div>
  );
};

// Section Card Component
interface SectionCardProps {
  title: string;
  subtitle?: string;
  icon?: IconType;
  children: ReactNode;
  action?: ReactNode;
  noPadding?: boolean;
}

export const SectionCard: React.FC<SectionCardProps> = ({
  title,
  subtitle,
  icon: Icon,
  children,
  action,
  noPadding = false,
}) => {
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-gray-500" />}
          <div>
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
          </div>
        </div>
        {action}
      </div>
      <div className={noPadding ? '' : 'p-4'}>{children}</div>
    </div>
  );
};

// Status Badge Component
interface StatusBadgeProps {
  status: string;
  size?: 'sm' | 'md';
}

export const StatusBadge: React.FC<StatusBadgeProps> = ({ status, size = 'sm' }) => {
  const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
    present: { bg: 'bg-green-100', text: 'text-green-700', label: 'Present' },
    absent: { bg: 'bg-red-100', text: 'text-red-700', label: 'Absent' },
    not_marked: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Not Marked' },
    pending_approval: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending Approval' },
    worked: { bg: 'bg-green-100', text: 'text-green-700', label: 'Worked' },
    idle: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Idle' },
    leave: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'On Leave' },
    weekly_off: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Weekly Off' },
    holiday: { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'Holiday' },
    pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending' },
    approved: { bg: 'bg-green-100', text: 'text-green-700', label: 'Approved' },
    rejected: { bg: 'bg-red-100', text: 'text-red-700', label: 'Rejected' },
    active: { bg: 'bg-green-100', text: 'text-green-700', label: 'Active' },
    completed: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Completed' },
    on_hold: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'On Hold' },
    cancelled: { bg: 'bg-red-100', text: 'text-red-700', label: 'Cancelled' },
    critical: { bg: 'bg-red-100', text: 'text-red-700', label: 'Critical' },
    high: { bg: 'bg-orange-100', text: 'text-orange-700', label: 'High' },
    medium: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Medium' },
    low: { bg: 'bg-green-100', text: 'text-green-700', label: 'Low' },
  };

  const normalizedStatus = status?.toLowerCase().replace(/\s+/g, '_');
  const config = statusConfig[normalizedStatus] || {
    bg: 'bg-gray-100',
    text: 'text-gray-700',
    label: status,
  };

  const sizeClasses = size === 'sm' ? 'px-2 py-0.5 text-xs' : 'px-3 py-1 text-sm';

  return (
    <span
      className={`inline-flex items-center rounded-full font-medium ${config.bg} ${config.text} ${sizeClasses}`}
    >
      {config.label}
    </span>
  );
};

// Data Table Component
interface Column<T> {
  key: keyof T | string;
  label: string;
  render?: (item: T) => ReactNode;
  align?: 'left' | 'center' | 'right';
}

interface DataTableProps<T> {
  columns: Column<T>[];
  data: T[];
  emptyMessage?: string;
  maxHeight?: string;
}

export function DataTable<T extends Record<string, any>>({
  columns,
  data,
  emptyMessage = 'No data available',
  maxHeight = '320px',
}: DataTableProps<T>) {
  const alignClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  return (
    <div className="overflow-x-auto" style={{ maxHeight }}>
      <table className="w-full">
        <thead className="bg-gray-50 sticky top-0">
          <tr>
            {columns.map((col) => (
              <th
                key={String(col.key)}
                className={`px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                  alignClasses[col.align || 'left']
                }`}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="px-3 py-8 text-center text-sm text-gray-500"
              >
                <FaExclamationCircle className="w-5 h-5 mx-auto mb-2 text-gray-400" />
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((item, index) => (
              <tr key={item.id || index} className="hover:bg-gray-50">
                {columns.map((col) => (
                  <td
                    key={String(col.key)}
                    className={`px-3 py-2 text-sm text-gray-700 ${
                      alignClasses[col.align || 'left']
                    }`}
                  >
                    {col.render
                      ? col.render(item)
                      : String((item as any)[col.key] ?? '-')}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// Progress Bar Component
interface ProgressBarProps {
  value: number;
  maxValue?: number;
  label?: string;
  showPercentage?: boolean;
  color?: 'blue' | 'green' | 'yellow' | 'red';
  size?: 'sm' | 'md';
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  value,
  maxValue = 100,
  label,
  showPercentage = true,
  color = 'blue',
  size = 'md',
}) => {
  const percentage = Math.min((value / maxValue) * 100, 100);
  const colors = {
    blue: 'bg-blue-600',
    green: 'bg-green-600',
    yellow: 'bg-yellow-500',
    red: 'bg-red-600',
  };
  const heights = {
    sm: 'h-1.5',
    md: 'h-2.5',
  };

  return (
    <div className="space-y-1">
      {(label || showPercentage) && (
        <div className="flex justify-between text-xs">
          {label && <span className="text-gray-600">{label}</span>}
          {showPercentage && (
            <span className="font-medium text-gray-900">{Math.round(percentage)}%</span>
          )}
        </div>
      )}
      <div className={`bg-gray-200 rounded-full overflow-hidden ${heights[size]}`}>
        <div
          className={`${colors[color]} ${heights[size]} rounded-full transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

// Loading Skeleton Components
export const SkeletonCard: React.FC = () => (
  <div className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
    <div className="flex items-start justify-between">
      <div className="flex-1">
        <div className="h-3 bg-gray-200 rounded w-20 mb-2" />
        <div className="h-6 bg-gray-200 rounded w-16 mb-2" />
        <div className="h-2 bg-gray-200 rounded w-24" />
      </div>
      <div className="w-11 h-11 bg-gray-200 rounded-lg" />
    </div>
  </div>
);

export const SkeletonChart: React.FC = () => (
  <div className="bg-white rounded-lg border border-gray-200 p-4 animate-pulse">
    <div className="h-4 bg-gray-200 rounded w-32 mb-4" />
    <div className="h-48 bg-gray-200 rounded" />
  </div>
);

export const SkeletonTable: React.FC = () => (
  <div className="bg-white rounded-lg border border-gray-200 animate-pulse">
    <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
      <div className="h-4 bg-gray-200 rounded w-32" />
    </div>
    <div className="p-4 space-y-3">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="flex gap-4">
          <div className="h-3 bg-gray-200 rounded flex-1" />
          <div className="h-3 bg-gray-200 rounded w-20" />
          <div className="h-3 bg-gray-200 rounded w-16" />
        </div>
      ))}
    </div>
  </div>
);

// Empty State Component
interface EmptyStateProps {
  icon?: IconType;
  title: string;
  description?: string;
  action?: ReactNode;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon: Icon,
  title,
  description,
  action,
}) => {
  return (
    <div className="flex flex-col items-center justify-center py-12 text-center">
      {Icon && (
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
          <Icon className="w-8 h-8 text-gray-400" />
        </div>
      )}
      <h3 className="text-sm font-medium text-gray-900">{title}</h3>
      {description && (
        <p className="mt-1 text-sm text-gray-500 max-w-sm">{description}</p>
      )}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
};

// Time display helper
export const TimeDisplay: React.FC<{ time?: string; placeholder?: string }> = ({
  time,
  placeholder = '--:--',
}) => {
  if (!time) {
    return <span className="text-gray-400">{placeholder}</span>;
  }

  return (
    <span className="inline-flex items-center gap-1 text-gray-700">
      <FaClock className="w-3 h-3 text-gray-400" />
      {time}
    </span>
  );
};

// Currency formatter
export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(amount);
};

// Date formatter
export const formatDate = (dateString: string, options?: Intl.DateTimeFormatOptions): string => {
  return new Date(dateString).toLocaleDateString('en-IN', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    ...options,
  });
};

// Format workflow status
export const formatWorkflowStatus = (status: string): string => {
  return status
    ?.toLowerCase()
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};
